import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import ProviderIcon from "@/components/ProviderIcon";
import { ShieldCheck, Sparkles, Database, Lock } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { user, signInWithGoogle, signInWithMicrosoft, signInDemo } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Surface OAuth errors coming back from the backend (?error=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) setError(decodeURIComponent(err));
  }, []);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/").then(({ data }) => setDemoEnabled(!!data.demo_mode));
    api.get("/providers").then(({ data }) => setProviders(data.providers));
  }, []);

  const googleReady = providers.find((p) => p.name === "google")?.configured;
  const msReady = providers.find((p) => p.name === "microsoft")?.configured;
  const anyReady = googleReady || msReady;

  const handleProvider = async (which) => {
    setBusy(true);
    setError("");
    try {
      if (which === "google") await signInWithGoogle();
      else await signInWithMicrosoft();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const handleDemo = async () => {
    setBusy(true);
    setError("");
    try {
      await signInDemo();
      navigate("/dashboard");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App">
      <Header variant="marketing" />
      <main className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-32">
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Login</div>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">
              {t("auth.welcome")}
            </h1>
            <p className="mt-4 max-w-md text-muted-foreground">{t("auth.welcomeSub")}</p>

            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-[#002FA7]" />
                OAuth Google / Microsoft · jamais de mot de passe
              </li>
              <li className="flex items-center gap-3 text-muted-foreground">
                <Lock className="h-4 w-4 text-[#002FA7]" />
                Tokens chiffrés au repos (Fernet)
              </li>
              <li className="flex items-center gap-3 text-muted-foreground">
                <Database className="h-4 w-4 text-[#002FA7]" />
                Vos fichiers ne sont jamais conservés
              </li>
            </ul>
          </div>

          <div className="border border-border bg-card p-8">
            <div className="font-mono text-xs uppercase text-muted-foreground">// auth</div>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="mt-2 text-xs text-muted-foreground">{t("auth.providerHint")}</p>

            {error && (
              <Alert className="mt-5 rounded-sm border-destructive/40 bg-destructive/10 text-destructive">
                <AlertDescription className="font-mono text-xs">{error}</AlertDescription>
              </Alert>
            )}

            {!anyReady && (
              <Alert className="mt-5 rounded-sm border-amber-500/40 bg-amber-500/10">
                <AlertDescription className="text-sm">
                  {t("auth.oauthDisabled")}
                </AlertDescription>
              </Alert>
            )}

            <div className="mt-6 space-y-3">
              <Button
                size="lg"
                disabled={!googleReady || busy}
                onClick={() => handleProvider("google")}
                className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90 h-12 gap-3 justify-center"
                data-testid="google-signin-button"
              >
                <ProviderIcon provider="google" className="h-4 w-4" />
                {busy ? t("auth.signingIn") : t("auth.signInGoogle")}
              </Button>

              <Button
                size="lg"
                variant="outline"
                disabled={!msReady || busy}
                onClick={() => handleProvider("microsoft")}
                className="w-full rounded-sm h-12 gap-3 justify-center border-foreground/20"
                data-testid="microsoft-signin-button"
              >
                <ProviderIcon provider="microsoft" className="h-4 w-4" />
                {busy ? t("auth.signingIn") : t("auth.signInMicrosoft")}
              </Button>

              {/* Per-provider pending state */}
              <div className="flex flex-wrap gap-2 pt-1 font-mono text-[10px]">
                {providers.map((p) => (
                  <span
                    key={p.name}
                    className={`flex items-center gap-1 border px-1.5 py-0.5 ${
                      p.configured
                        ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    <ProviderIcon provider={p.name} className="h-2.5 w-2.5" />
                    {p.label} · {p.configured ? "ready" : "pending"}
                  </span>
                ))}
              </div>
            </div>

            {demoEnabled && (
              <>
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">{t("auth.orSeparator")}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <Button
                  size="lg"
                  variant="outline"
                  disabled={busy}
                  onClick={handleDemo}
                  className="w-full rounded-sm h-12 gap-2 border-dashed border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7]/5"
                  data-testid="demo-signin-button"
                >
                  <Sparkles className="h-4 w-4" />
                  {t("auth.signInDemo")}
                </Button>
              </>
            )}

            <p className="mt-6 font-mono text-[11px] text-muted-foreground leading-relaxed">
              Scopes Google : <span className="text-foreground">drive</span>,{" "}
              <span className="text-foreground">userinfo.email</span>,{" "}
              <span className="text-foreground">userinfo.profile</span>.
              <br />
              Scopes Microsoft : <span className="text-foreground">Files.ReadWrite.All</span>,{" "}
              <span className="text-foreground">User.Read</span>,{" "}
              <span className="text-foreground">offline_access</span>.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
